import type { HTMLAttributes, ReactNode } from "react";

import { cn } from "../lib/cn";

export type BadgeTone = "neutral" | "accent" | "success" | "warning" | "danger";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  children: ReactNode;
  tone?: BadgeTone;
  dot?: boolean;
}

const toneClasses: Record<BadgeTone, string> = {
  neutral: "border-border bg-surface-elevated text-secondary",
  accent: "border-brand-cyan/20 bg-brand-cyan/10 text-interaction-text",
  success: "border-status-ok/30 bg-status-ok-bg text-status-ok-text",
  warning: "border-status-warn/30 bg-status-warn-bg text-status-warn-text",
  danger: "border-status-error/30 bg-status-error-bg text-status-error-text",
};

const dotClasses: Record<BadgeTone, string> = {
  neutral: "bg-muted",
  accent: "bg-brand-cyan",
  success: "bg-status-ok",
  warning: "bg-status-warn",
  danger: "bg-status-error",
};

export function Badge({ children, tone = "neutral", dot = false, className, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-md border px-2 py-0.5 text-xs font-medium",
        toneClasses[tone],
        className,
      )}
      {...props}
    >
      {dot ? <span className={cn("h-1.5 w-1.5 rounded-full", dotClasses[tone])} aria-hidden /> : null}
      {children}
    </span>
  );
}